/**
 * Room Page
 *
 * Main room view with chat, PTT, and quick actions.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useP2PContext } from '../contexts'
import { useGeolocation } from '../hooks/useGeolocation'
import { usePTT } from '../hooks/usePTT'
import { useRoom } from '../hooks/useRoom'
import { useIdentity } from '../hooks/useIdentity'
import { useBluetoothRoomBridge } from '../hooks/useBluetoothRoomBridge'
import { useHierarchy } from '../hooks/useHierarchy'
import { ConnectionStatus } from '../components/common'
import { PTTButton, QuickActions, Chat, GeofenceStatus, OrganizerControls, MuteButton } from '../components/Room'
import { BroadcastBanner, CommandDashboard } from '../components/Hierarchy'
import { SightingForm } from '../components/Vehicles/SightingForm'
import { LoRaStatusCard } from '../components/LoRa/LoRaStatus'
import { DeviceSetupPanel } from '../components/LoRa/DeviceSetupPanel'
import { ENFORCEMENT_REPORTS } from '../lib/features'
import { playAudio } from '../lib/audio'
import { POIMiniMap } from '../components/Map/POIMiniMap'
import type { QuickActionKey, Role, RoomMeta, AllowedTransportsConfig, HierarchyRoomType } from '../lib/types'
import { DEFAULT_ALLOWED_TRANSPORTS } from '../lib/types'
import { getRoom, type StoredRoom } from '../lib/storage'
import { fuzzLocation, getTransportAwareGeofenceStatus } from '../lib/geo'
import { LocationAccuracyToggle } from '../components/common/LocationAccuracyToggle'
import {
  getStoredAccuracyMode,
  type LocationAccuracyMode,
} from '../components/common/locationAccuracyUtils'
import { useLoRa } from '../hooks/useLoRa'
import { useLoRaRoomBridge } from '../hooks/useLoRaRoomBridge'
import { meshcoreSession, type RadioState } from '../lib/meshcore'

export function RoomPage() {
  const { roomId } = useParams<{ roomId: string }>()
  const navigate = useNavigate()
  const {
    node,
    status,
    bluetoothAvailable,
    bluetoothEnabled,
    enableBluetooth,
    disableBluetooth,
    scanBluetooth,
    setAllowedTransports: setContextAllowedTransports,
  } = useP2PContext()

  const [storedRoom, setStoredRoom] = useState<StoredRoom | null>(null)
  const [displayName, setDisplayName] = useState('')
  const [roomSecret, setRoomSecret] = useState('')
  const [roomPin, setRoomPin] = useState<string | undefined>(undefined)
  const [textInput, setTextInput] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [accuracyMode, setAccuracyMode] = useState<LocationAccuracyMode>(() =>
    getStoredAccuracyMode()
  )
  const [isMuted, setIsMuted] = useState(false)
  const [isCommsPanelExpanded, setIsCommsPanelExpanded] = useState(false)
  const [showSightingForm, setShowSightingForm] = useState(false)
  const [showRadioPanel, setShowRadioPanel] = useState(false)
  const [radioState, setRadioState] = useState<RadioState>(meshcoreSession.snapshot)
  useEffect(() => meshcoreSession.onState(setRadioState), [])
  // Give the network a moment after opening the room before calling it offline.
  const [pathSettled, setPathSettled] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setPathSettled(true), 10_000)
    return () => clearTimeout(t)
  }, [])
  const [allowedTransports, setAllowedTransports] = useState<AllowedTransportsConfig>(
    DEFAULT_ALLOWED_TRANSPORTS
  )


  // Geolocation with user's accuracy preference
  const {
    location,
    startWatching,
    stopWatching,
  } = useGeolocation({
    accuracyMode,
    watchPosition: true,
    enableIpFallback: true,
  })

  // Re-request location when accuracy mode changes
  const handleAccuracyChange = useCallback(
    (mode: LocationAccuracyMode) => {
      setAccuracyMode(mode)
      stopWatching()
      setTimeout(() => {
        startWatching()
      }, 100)
    },
    [stopWatching, startWatching]
  )

  // Load room data on mount
  useEffect(() => {
    if (!roomId) {
      navigate('/')
      return
    }

    const loadRoom = async () => {
      try {
        // Get display name from session
        const savedName = sessionStorage.getItem('displayName')
        if (!savedName) {
          navigate('/join/' + roomId)
          return
        }
        setDisplayName(savedName)

        // Get room secret from session
        const secret = sessionStorage.getItem(`room_${roomId}_secret`)
        if (!secret) {
          setError('Room secret not found. Please rejoin via link.')
          setIsLoading(false)
          return
        }
        setRoomSecret(secret)

        // Get room PIN from session (if any - for participants joining with PIN)
        const pin = sessionStorage.getItem(`room_${roomId}_pin`)
        if (pin) {
          setRoomPin(pin)
        }

        // Get room from storage
        const room = await getRoom(roomId)
        if (room) {
          setStoredRoom(room)
        }

        // Get allowed transports from session (organizer sets this at creation)
        const storedTransports = sessionStorage.getItem(`room_${roomId}_allowedTransports`)
        if (storedTransports) {
          try {
            const parsed = JSON.parse(storedTransports) as AllowedTransportsConfig
            setAllowedTransports(parsed)
          } catch {
            // Invalid JSON, use defaults
          }
        }

        startWatching()
        setIsLoading(false)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load room')
        setIsLoading(false)
      }
    }

    loadRoom()
  }, [roomId, navigate, startWatching])



  // Determine role - check session storage first (survives IndexedDB clearing), then fallback to storedRoom
  const sessionRole = sessionStorage.getItem(`room_${roomId}_role`) as Role | null
  const myRole: Role = sessionRole ?? (storedRoom ? 'organizer' : 'participant')

  // Get or create cryptographic identity for this room
  const { identity, isLoading: identityLoading, error: identityError } = useIdentity({
    roomId: roomId ?? '',
    displayName,
    role: myRole,
  })

  // Build initial room metadata for organizer to initialize CRDT
  // IMPORTANT: Memoize to prevent infinite re-renders
  const initialRoomMeta: RoomMeta | undefined = useMemo(() => {
    if (!storedRoom) return undefined
    return {
      name: storedRoom.name,
      center: { latitude: storedRoom.centerLat, longitude: storedRoom.centerLng },
      radiusMeters: storedRoom.radiusMeters,
      createdAt: storedRoom.createdAt,
      micMode: 'open' as const,
      chatMode: 'open' as const,
      poiMode: 'open' as const,
      poiExpirationMinutes: 15,
      isPaused: false,
      sharingEnabled: true,
      allowedTransports,
    }
  }, [storedRoom, allowedTransports])

  // Room state management
  // Use PIN from sessionStorage (for participants) or from storedRoom (for organizers)
  const effectivePin = roomPin ?? storedRoom?.pin
  const roomState = useRoom({
    roomId: roomId ?? '',
    roomSecret,
    roomPin: effectivePin,
    node,
    identity,
    initialRoomMeta,
  })

  // Bluetooth mesh bridge for offline P2P
  useBluetoothRoomBridge({
    roomId: roomId ?? '',
    roomDoc: roomState.roomDoc,
    enabled: bluetoothEnabled,
  })

  // LoRa mesh integration
  const loRa = useLoRa()

  // LoRa room bridge for syncing room messages over LoRa mesh
  // Respect room's configured allowedTransports (synced from organizer), fallback to local config
  useLoRaRoomBridge({
    roomId: roomId ?? '',
    roomSecret,
    roomName: roomState.roomMeta?.name ?? storedRoom?.name,
    roomDoc: roomState.roomDoc,
    enabled: loRa.isReady && (roomState.roomMeta?.allowedTransports?.lora ?? allowedTransports.lora ?? true),
  })

  // Hierarchy state - check session storage for hierarchy type
  const [hierarchyType, setHierarchyType] = useState<HierarchyRoomType>(() => {
    const stored = sessionStorage.getItem(`room_${roomId}_hierarchyType`)
    return (stored as HierarchyRoomType) ?? 'standalone'
  })
  const [commandRoomId] = useState<string | undefined>(() => {
    return sessionStorage.getItem(`room_${roomId}_commandRoomId`) ?? undefined
  })

  // Hierarchy management
  const hierarchy = useHierarchy({
    roomId: roomId ?? '',
    commandRoomId,
    node,
    identity,
    hierarchyType,
  })

  // Handler to enable command mode
  const handleEnableCommandMode = useCallback(() => {
    if (!identity) return
    setHierarchyType('command')
    sessionStorage.setItem(`room_${roomId}_hierarchyType`, 'command')
    hierarchy.initializeAsCommand(identity.publicKeyBase64)
  }, [roomId, identity, hierarchy])

  // Handler to create a sector
  const handleCreateSector = useCallback((params: { name: string; center: { latitude: number; longitude: number }; radiusMeters: number }) => {
    // Generate a new sector room ID
    const sectorRoomId = crypto.randomUUID()
    const result = hierarchy.addSector(
      sectorRoomId,
      params.name,
      { latitude: params.center.latitude, longitude: params.center.longitude },
      params.radiusMeters
    )
    if (!result.success) {
      console.error('[Room] Failed to create sector:', result.error)
      alert(`Failed to create sector: ${result.error}`)
    } else {
      // Store the sector secret in session storage for sharing
      if (result.sectorSecret) {
        sessionStorage.setItem(`sector_${sectorRoomId}_secret`, result.sectorSecret)
      }
      console.log('[Room] Created sector:', sectorRoomId, params, 'with secret:', !!result.sectorSecret)
    }
  }, [hierarchy])

  // Handler to open a sector (navigate to it)
  const handleOpenSector = useCallback((sectorRoomId: string) => {
    // In a full implementation, this would navigate to the sector room
    console.log('[Room] Opening sector:', sectorRoomId)
    // For now, just log - would need sector room secrets to actually join
  }, [])

  // Handler to remove/delete a sector
  const handleRemoveSector = useCallback((sectorRoomId: string) => {
    // Remove sector secret from session storage
    sessionStorage.removeItem(`sector_${sectorRoomId}_secret`)
    // Remove from hierarchy
    hierarchy.removeSector(sectorRoomId)
    console.log('[Room] Removed sector:', sectorRoomId)
  }, [hierarchy])

  // PTT
  const ptt = usePTT({
    enableTranscription: true,
    onRecordingEnd: (result) => {
      if (result) {
        // Send the voice message to room
        roomState.sendVoiceMessage(
          result.audioBlob,
          result.duration,
          result.transcript,
          result.transcriptConfidence
        )

        // Auto-play the recording as confirmation
        playAudio(result.audioBlob, {
          onError: (err) => console.error('[Room] Failed to auto-play recording:', err),
        }).catch(() => {
          // Ignore playback errors - message was still sent
        })
      }
    },
  })

  // Geofence status - use roomMeta from CRDT (synced), fallback to storedRoom
  // With LoRa extended boundary support (10km when LoRa is connected)
  const roomCenter = roomState.roomMeta?.center ?? (storedRoom ? { latitude: storedRoom.centerLat, longitude: storedRoom.centerLng } : null)
  const roomRadius = roomState.roomMeta?.radiusMeters ?? storedRoom?.radiusMeters ?? null
  const geofenceStatus = roomCenter && roomRadius && location
    ? getTransportAwareGeofenceStatus({
        point: location,
        center: roomCenter,
        radiusMeters: roomRadius,
        isLoRaConnected: loRa.isConnected,
      })
    : null

  // Update participant's geofence status in CRDT when it changes
  const isInsideGeofence = geofenceStatus?.isInside
  const { updateMyGeofenceStatus } = roomState
  useEffect(() => {
    if (isInsideGeofence !== undefined) {
      updateMyGeofenceStatus(isInsideGeofence)
    }
  }, [isInsideGeofence, updateMyGeofenceStatus])

  // Sync room's allowed transports to P2P context
  // This controls which transports the P2P layer will use
  useEffect(() => {
    // Use room meta's allowed transports if available (synced from organizer)
    // Otherwise use locally stored allowed transports
    const effectiveTransports = roomState.roomMeta?.allowedTransports ?? allowedTransports
    setContextAllowedTransports(effectiveTransports)
  }, [roomState.roomMeta?.allowedTransports, allowedTransports, setContextAllowedTransports])

  // Handlers
  const handleSendText = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      if (textInput.trim()) {
        roomState.sendTextMessage(textInput.trim())
        setTextInput('')
      }
    },
    [textInput, roomState]
  )

  const handleQuickAction = useCallback(
    (action: QuickActionKey) => {
      // Vehicle sighting opens a dedicated form instead of sending a generic POI
      if (action === 'vehicle_sighting') {
        setShowSightingForm(true)
        return
      }

      if (location) {
        const fuzzed = fuzzLocation(location)
        roomState.sendPOI(action, {
          latitude: fuzzed.latitude,
          longitude: fuzzed.longitude,
        })
      }
    },
    [location, roomState]
  )

  // Handle vehicle sighting form submission
  const handleSightingSubmit = useCallback(
    (sighting: {
      location: { latitude: number; longitude: number }
      plate?: string
      plateConfidence: 'full' | 'partial' | 'none'
      vehicleType?: 'sedan' | 'suv' | 'van' | 'truck' | 'unmarked' | 'other'
      vehicleColor?: string
      direction?: 'N' | 'NE' | 'E' | 'SE' | 'S' | 'SW' | 'W' | 'NW' | 'stationary'
      description?: string
      isFollowing: boolean
      photos?: Array<{
        id: string
        thumbnailDataUrl: string
        location?: { latitude: number; longitude: number }
        plateOCR?: { text: string; confidence: number }
      }>
    }) => {
      roomState.sendVehicleSighting({
        location: sighting.location,
        plate: sighting.plate,
        plateConfidence: sighting.plateConfidence,
        vehicleType: sighting.vehicleType,
        vehicleColor: sighting.vehicleColor,
        direction: sighting.direction,
        description: sighting.description,
        isFollowing: sighting.isFollowing,
        photos: sighting.photos?.map(p => ({
          id: p.id,
          thumbnailDataUrl: p.thumbnailDataUrl,
          hasLocation: !!p.location,
          plateOCR: p.plateOCR,
        })),
      })
      setShowSightingForm(false)
    },
    [roomState]
  )

  const handleLeave = useCallback(() => {
    roomState.leave()
    navigate('/')
  }, [roomState, navigate])

  const handleEndRoom = useCallback(() => {
    roomState.endRoom()
    // Give time for the end message to sync, then redirect
    setTimeout(() => {
      roomState.leave()
      navigate('/')
    }, 1000)
  }, [roomState, navigate])

  // Redirect if room was ended
  useEffect(() => {
    if (roomState.isEnded && myRole !== 'organizer') {
      // Non-organizers leave when room ends
      setTimeout(() => {
        roomState.leave()
        navigate('/')
      }, 2000)
    }
  }, [roomState.isEnded, myRole, roomState, navigate])

  // Loading state
  if (isLoading || identityLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4 animate-pulse">📻</div>
          <div className="text-white/60">
            {identityLoading ? 'Creating secure identity...' : 'Connecting to room...'}
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (error || identityError) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="text-4xl mb-4">⚠️</div>
          <div className="text-red-400 mb-4">{error ?? identityError?.message}</div>
          <Link
            to="/"
            className="text-primary hover:underline"
          >
            Go back home
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden font-body">
      {/* Main Header - Compact */}
      <header className="flex-shrink-0 px-3 py-2 bg-secondary/80 backdrop-blur-md z-20 border-b border-white/5 shadow-lg relative">
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-transparent pointer-events-none"></div>
        <div className="flex items-center justify-between relative z-10">
          {/* Left: Leave button */}
          <button
            onClick={handleLeave}
            className="group h-8 px-2 flex items-center gap-1.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 rounded active:scale-95 transition-all"
          >
            <span className="material-icons-round text-red-500 text-base">power_settings_new</span>
            <span className="text-[10px] font-bold text-red-500 uppercase hidden sm:inline">Exit</span>
          </button>

          {/* Center: Room info */}
          <div
            className="text-center cursor-pointer"
            onClick={() => {
              const debugInfo = `roomId: ${roomId}\nsecret: ${roomSecret?.substring(0, 8)}...\nrole: ${myRole}\npeerId: ${identity?.peerId?.substring(0, 12)}...`
              navigator.clipboard.writeText(debugInfo)
            }}
          >
            <h1 className="font-display font-bold text-base tracking-wider">
              <span className="text-white/80">CT</span><span className="text-primary">:</span>
              <span className="text-white/60 text-xs ml-1">{roomState.roomMeta?.name ?? storedRoom?.name ?? 'Room'}</span>
            </h1>
          </div>

          {/* Right: Status indicators */}
          <div className="flex items-center gap-2">
            <div className="px-1.5 py-0.5 bg-primary/10 rounded text-[10px] font-bold border border-primary/30 flex items-center">
              <span className="material-icons-round text-[10px] mr-0.5 text-primary">group</span>
              <span className="text-primary">{roomState.participants.length}</span>
            </div>
            <ConnectionStatus
              status={status}
              bluetoothAvailable={bluetoothAvailable}
              bluetoothEnabled={bluetoothEnabled}
              onEnableBluetooth={enableBluetooth}
              onDisableBluetooth={disableBluetooth}
              onScanBluetooth={scanBluetooth}
              allowedTransports={roomState.roomMeta?.allowedTransports ?? allowedTransports}
            />
            {/* LoRa Status - shows when connecting or connected */}
            {(loRa.isConnecting || loRa.isConnected) ? (
              <LoRaStatusCard
                status={loRa.status}
                device={loRa.device}
                onClick={() => setShowRadioPanel(true)}
              />
            ) : (
              loRa.isAvailable &&
              (roomState.roomMeta?.allowedTransports?.lora ?? allowedTransports.lora ?? true) && (
                <button
                  onClick={() => setShowRadioPanel(true)}
                  className="px-1.5 py-0.5 rounded text-[10px] font-bold border border-purple-500/40 bg-purple-500/10 text-purple-300 hover:bg-purple-500/20"
                  aria-label="Connect a LoRa radio"
                >
                  📻 Radio
                </button>
              )
            )}
            <GeofenceStatus
              status={geofenceStatus}
              hasLocation={!!location}
              hasRoomData={!!(roomCenter && roomRadius)}
              isLoRaConnected={loRa.isConnected}
            />
          </div>
        </div>
      </header>

      {/* Command Dashboard - shows for command rooms when organizer/coordinator */}
      {hierarchyType === 'command' && (myRole === 'organizer' || myRole === 'coordinator') && (
        <div className="flex-shrink-0 px-3 py-2 border-b border-white/5 bg-secondary/90 backdrop-blur-sm z-10">
          <CommandDashboard
            sectors={hierarchy.sectors}
            extendedSectors={hierarchy.sectors}
            pendingEscalations={hierarchy.pendingEscalations}
            totalParticipants={hierarchy.sectors.reduce((sum, s) => sum + s.participantCount, 0) + roomState.participants.length}
            onAcknowledgeEscalation={hierarchy.acknowledgeEscalation}
            onBroadcast={hierarchy.broadcastToSectors}
            onOpenSector={handleOpenSector}
            commandBoundary={roomCenter && roomRadius ? { center: roomCenter, radiusMeters: roomRadius } : undefined}
            userLocation={location}
            aggregatedPOIs={roomState.activePOIs}
            onAssignOrganizer={myRole === 'organizer' ? hierarchy.assignOrganizerToSector : undefined}
            onUpdateSectorBoundary={myRole === 'organizer' ? (sectorRoomId, newCenter, newRadius) =>
              hierarchy.updateSectorBoundary(sectorRoomId, newCenter, newRadius) : undefined
            }
            onSendCommand={myRole === 'organizer' ? hierarchy.sendRemoteCommand : undefined}
            sectorParticipants={{}}
            roomName={roomState.roomMeta?.name ?? storedRoom?.name ?? 'Command Room'}
            roomId={roomId ?? ''}
            roomSecret={myRole === 'organizer' ? roomSecret : ''}
            roomPin={myRole === 'organizer' ? storedRoom?.pin : undefined}
            onCreateSector={myRole === 'organizer' ? handleCreateSector : undefined}
            onRemoveSector={myRole === 'organizer' ? handleRemoveSector : undefined}
          />
        </div>
      )}

      {/* Organizer Controls (Room Controls) */}
      {roomState.roomMeta && (
        <div className="flex-shrink-0 px-3 py-2 border-b border-white/5 bg-secondary/90 backdrop-blur-sm z-10">
          <OrganizerControls
            roomMeta={roomState.roomMeta}
            roomId={roomId ?? ''}
            roomSecret={roomSecret}
            roomPin={storedRoom?.pin}
            participants={roomState.participants}
            myRole={myRole}
            currentLocation={location}
            onUpdateMeta={roomState.updateMeta}
            onUpdateParticipantRole={roomState.updateParticipantRole}
            onKickParticipant={roomState.kickParticipant}
            onEndRoom={handleEndRoom}
            // Hierarchy props
            hierarchyType={hierarchyType}
            sectors={hierarchy.sectors}
            onEnableCommandMode={handleEnableCommandMode}
          />
        </div>
      )}

      {/* Broadcast Banner - shows incoming command broadcasts for sector rooms */}
      {hierarchyType === 'sector' && hierarchy.recentBroadcasts[0] && (
        <div className="flex-shrink-0 z-10">
          <BroadcastBanner
            broadcast={hierarchy.recentBroadcasts[0]}
            onDismiss={() => {
              // Could implement dismiss logic here
            }}
          />
        </div>
      )}

      {/* POI MiniMap - skip for command rooms (they have their own map in dashboard) */}
      {hierarchyType !== 'command' && (roomState.roomMeta || storedRoom || location) && (
        <div className="flex-shrink-0 px-3 py-2 border-b border-white/5 bg-black/20 z-10">
          <POIMiniMap
            center={
              roomState.roomMeta?.center ??
              (storedRoom ? { latitude: storedRoom.centerLat, longitude: storedRoom.centerLng } : null) ??
              location ??
              { latitude: 0, longitude: 0 }
            }
            radiusMeters={roomState.roomMeta?.radiusMeters ?? storedRoom?.radiusMeters ?? 500}
            userLocation={location}
            pois={roomState.activePOIs}
            onClearPOI={roomState.clearPOI}
            canClearPOIs={['organizer', 'coordinator'].includes(myRole)}
            hierarchyType={hierarchyType}
            showSiblings={hierarchyType === 'sector'}
          />
        </div>
      )}

      {/* Room Ended Banner */}
      {roomState.isEnded && (
        <div className="flex-shrink-0 px-3 py-2 bg-red-500/20 border-b border-red-500/30 z-20">
          <div className="text-center text-red-400 font-medium animate-pulse font-mono uppercase tracking-wider">
            Room Closed
            {myRole !== 'organizer' && <span className="text-sm ml-2 normal-case">Redirecting...</span>}
          </div>
        </div>
      )}

      {/* Geofence Warning Overlay - blocks room when outside boundary (unless within LoRa extended boundary when connected) */}
      {geofenceStatus && !geofenceStatus.isInside && (
        <div className="absolute inset-0 bg-black/95 z-50 flex items-center justify-center p-6 backdrop-blur-sm">
          <div className="text-center max-w-sm">
            <span className="material-icons-round text-6xl text-yellow-500 mb-4 animate-pulse">location_off</span>
            <h2 className="text-xl font-bold text-yellow-500 mb-2 uppercase tracking-widest">Outside Room Area</h2>
            <div className="h-px w-20 bg-yellow-500/50 mx-auto mb-4"></div>
            <p className="text-gray-400 text-sm mb-4 font-mono">
              You are <span className="text-yellow-400 font-bold">+{geofenceStatus.distanceBeyondRadius ? Math.round(geofenceStatus.distanceBeyondRadius) : 0}m</span> outside the room boundary.
            </p>
            <p className="text-gray-500 text-xs mb-6">
              Move closer to the room's location to participate. The room is centered at the organizer's chosen location.
            </p>
            {/* LoRa hint - suggest connecting via LoRa if available */}
            {loRa.isAvailable && !loRa.isConnected && (allowedTransports.lora ?? true) && (
              <div className="mb-6 p-3 rounded-lg bg-purple-500/10 border border-purple-500/30">
                <p className="text-purple-400 text-xs mb-2 font-medium">
                  📻 LoRa device nearby?
                </p>
                <p className="text-purple-300/70 text-xs mb-3">
                  Connect via LoRa to extend your range up to 10km.
                </p>
                <button
                  onClick={() => loRa.connect()}
                  className="px-4 py-2 bg-purple-500/20 border border-purple-500/50 rounded-lg text-purple-400 text-xs font-bold hover:bg-purple-500/30 transition-all"
                >
                  Connect LoRa Device
                </button>
              </div>
            )}
            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  if (roomCenter) {
                    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${roomCenter.latitude},${roomCenter.longitude}`
                    window.open(mapsUrl, '_blank')
                  }
                }}
                className="px-6 py-3 bg-yellow-500/10 border border-yellow-500/50 rounded-xl text-yellow-500 font-bold hover:bg-yellow-500/20 transition-all uppercase tracking-widest"
              >
                Get Directions
              </button>
              <button
                onClick={handleLeave}
                className="px-6 py-3 bg-red-500/10 border border-red-500/50 rounded-xl text-red-500 font-bold hover:bg-red-500/20 transition-all uppercase tracking-widest"
              >
                Leave Room
              </button>
            </div>
          </div>
        </div>
      )}

      {/* How messages are leaving this phone right now */}
      {(() => {
        const netPath = status.peerCount > 0 || status.websocketConnected || status.webrtcConnected || status.bluetoothConnected
        if (netPath) return null
        if (radioState.status === 'ready') {
          return (
            <div className="flex-shrink-0 px-3 py-1.5 bg-purple-500/15 border-b border-purple-500/30 text-[11px] text-purple-200 flex items-center gap-2">
              <span aria-hidden>📡</span>
              <span>
                <strong>Offline: text over radio only.</strong>{' '}
                {radioState.bandProblem
                  ? radioState.bandProblem
                  : `Messages go out one every 5 s${radioState.queued ? ` (${radioState.queued} waiting)` : ''}. Voice needs Wi-Fi or internet.`}
              </span>
            </div>
          )
        }
        if (!pathSettled) return null
        return (
          <div className="flex-shrink-0 px-3 py-1.5 bg-yellow-500/10 border-b border-yellow-500/30 text-[11px] text-yellow-200 flex items-center gap-2">
            <span aria-hidden>⚠️</span>
            <span className="flex-1">
              <strong>No connection.</strong> Nothing you send is reaching anyone yet; it will sync when a connection
              returns.
            </span>
            {loRa.isAvailable && (roomState.roomMeta?.allowedTransports?.lora ?? allowedTransports.lora ?? true) && (
              <button
                onClick={() => setShowRadioPanel(true)}
                className="shrink-0 min-h-[32px] px-2 rounded bg-purple-500/20 border border-purple-500/40 text-purple-200 font-bold"
              >
                📻 Use a radio
              </button>
            )}
          </div>
        )
      })()}

      {/* Chat Area - flex-1 with min-h-0 allows proper shrinking, scrolls internally */}
      <div className="flex-1 min-h-0 overflow-hidden relative z-0">
        <Chat
          messages={roomState.messages}
          myPeerId={identity?.peerId ?? ''}
          className="h-full"
          autoPlayVoice={!isMuted}
        />
      </div>

      {/* Footer / Input Area - Collapsible COMMS PANEL */}
      <div
        className="relative flex-shrink-0 z-40 rounded-t-2xl flex flex-col border-t-2 border-white/10 shadow-[0_-5px_30px_rgba(0,0,0,0.7)] overflow-hidden"
        style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}
      >
        {/* Tactical panel background with texture */}
        <div className="absolute inset-0 bg-[#0d0d0d] bg-[radial-gradient(circle_at_50%_0%,_rgba(242,13,128,0.05)_0%,_transparent_50%),repeating-linear-gradient(0deg,_transparent_0px,_transparent_2px,_rgba(255,255,255,0.02)_2px,_rgba(255,255,255,0.02)_4px)]" />

        {/* Top edge highlight */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />

        {/* Content wrapper */}
        <div className="relative px-3 pt-2">
          {/* Compact header row with expand toggle */}
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={() => setIsCommsPanelExpanded(!isCommsPanelExpanded)}
              className="flex items-center gap-2 text-white/40 hover:text-white/60 transition-colors"
            >
              <div className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-pulse shadow-[0_0_6px_theme('colors.primary')]" />
              <span className="text-[8px] font-mono uppercase tracking-[0.2em]">COMMS</span>
              <span className="text-[10px]">{isCommsPanelExpanded ? '▼' : '▲'}</span>
            </button>

            {/* Inline controls when collapsed */}
            {!isCommsPanelExpanded && (
              <div className="flex items-center gap-2">
                <MuteButton
                  isMuted={isMuted}
                  onToggle={() => setIsMuted(!isMuted)}
                />
                <span className="text-[10px] font-mono text-white/30">CH:{(roomId ?? '').substring(0, 2).toUpperCase()}</span>
              </div>
            )}
          </div>

          {/* Compact input row - always visible */}
          <form onSubmit={handleSendText} className="flex gap-2 mb-2 relative z-50">
            <input
              type="text"
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              placeholder="Type message..."
              className="flex-1 bg-black/60 border border-white/10 px-3 py-2 text-sm text-white placeholder-white/20 focus:outline-none focus:border-primary/50 focus:bg-primary/5 transition-all font-mono rounded"
              onFocus={(e) => {
                e.target.scrollIntoView({ behavior: "smooth" })
                setIsCommsPanelExpanded(true)
              }}
            />
            <button
              type="submit"
              disabled={!textInput.trim()}
              className="px-3 bg-primary/20 hover:bg-primary/30 text-primary border border-primary/30 rounded transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <span className="material-icons-round text-lg">send</span>
            </button>
            <button
              type="button"
              onClick={() => setIsCommsPanelExpanded(!isCommsPanelExpanded)}
              className="px-2 bg-white/5 hover:bg-white/10 text-white/60 border border-white/10 rounded transition-all"
            >
              <span className="material-icons-round text-lg">{isCommsPanelExpanded ? 'expand_more' : 'expand_less'}</span>
            </button>
          </form>

          {/* Expanded content */}
          {isCommsPanelExpanded && (
            <div className="space-y-3 pb-2">
              {/* Quick Actions */}
              <QuickActions
                onAction={handleQuickAction}
                className="w-full"
                disabled={!location || (geofenceStatus ? !geofenceStatus.isInside : false)}
              />

              {/* Location accuracy toggle */}
              <div className="flex items-center justify-center">
                <LocationAccuracyToggle
                  value={accuracyMode}
                  onChange={handleAccuracyChange}
                  compact
                />
              </div>

              {/* PTT Control Row - Mute + PTT + Channel indicator */}
              <div className="flex items-center justify-center gap-4 relative z-40">
                {/* Mute Button - Left side */}
                <div className="flex flex-col items-center">
                  <MuteButton
                    isMuted={isMuted}
                    onToggle={() => setIsMuted(!isMuted)}
                  />
                  <span className="text-[7px] font-mono text-white/20 mt-1 uppercase tracking-widest">SPEAKER</span>
                </div>

                {/* PTT Button - Center */}
                <div className="flex flex-col items-center">
                  <PTTButton
                    isRecording={ptt.isRecording}
                    isDisabled={
                      (!ptt.hasPermission && !ptt.isRecording) ||
                      (radioState.status === 'ready' &&
                        status.peerCount === 0 &&
                        !status.websocketConnected &&
                        !status.webrtcConnected &&
                        !status.bluetoothConnected &&
                        !ptt.isRecording)
                    }
                    recordingDuration={ptt.recordingDuration}
                    onStart={ptt.startPTT}
                    onStop={ptt.stopPTT}
                  />
                </div>

                {/* Channel/Frequency indicator - Right side */}
                <div className="flex flex-col items-center w-14">
                  <div className="w-14 h-14 rounded-lg border-2 border-white/10 bg-[#0a0a0a] flex flex-col items-center justify-center shadow-[inset_0_2px_8px_rgba(0,0,0,0.8)]">
                    <span className="text-[8px] font-mono text-white/30 uppercase tracking-wider">CH</span>
                    <span className="text-lg font-mono font-bold text-primary/80 leading-none">{(roomId ?? '').substring(0, 2).padStart(2, '0').toUpperCase() || '01'}</span>
                    <span className="text-[7px] font-mono text-white/20 uppercase">MHz</span>
                  </div>
                  <span className="text-[7px] font-mono text-white/20 mt-1 uppercase tracking-widest">CHANNEL</span>
                </div>
              </div>

              {/* Permission Prompt */}
              {!ptt.hasPermission && (
                <div className="flex justify-center">
                  <button
                    onClick={ptt.requestPermission}
                    className="px-4 py-2 rounded-full bg-primary text-black text-xs font-bold shadow-[0_0_15px_theme('colors.primary')] animate-pulse uppercase tracking-widest border border-white/20"
                  >
                    Enable Microphone
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Transcript Toast */}
        {ptt.isRecording && ptt.currentTranscript && (
          <div className="absolute bottom-24 left-4 right-4 bg-black/80 backdrop-blur text-white text-xs p-3 rounded-lg text-center border-l-2 border-primary animate-slide-up z-50 font-mono shadow-lg">
            <span className="text-primary mr-2"> [REC] </span>{ptt.currentTranscript}
          </div>
        )}
      </div>

      {/* Loading Overlay */}
      {(isLoading || identityLoading) && (
        <div className="absolute inset-0 bg-black/90 z-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4 mx-auto shadow-[0_0_20px_theme('colors.primary')]"></div>
            <div className="text-primary font-display font-medium animate-pulse tracking-widest">Connecting...</div>
          </div>
        </div>
      )}

      {/* Error Overlay */}
      {(error || identityError) && (
        <div className="absolute inset-0 bg-black/95 z-50 flex items-center justify-center p-6">
          <div className="text-center">
            <span className="material-icons-round text-6xl text-red-500 mb-4 animate-pulse">warning</span>
            <h2 className="text-xl font-bold text-red-500 mb-2 uppercase tracking-widest">Connection Failed</h2>
            <div className="h-px w-20 bg-red-500/50 mx-auto mb-4"></div>
            <p className="text-gray-400 text-sm mb-8 font-mono">{error ?? (identityError as unknown as Error)?.message}</p>
            <Link to="/" className="px-8 py-4 bg-red-500/10 border border-red-500/50 rounded-xl text-red-500 font-bold hover:bg-red-500/20 transition-all uppercase tracking-widest shadow-[0_0_20px_rgba(239,68,68,0.2)]">
              Go Back
            </Link>
          </div>
        </div>
      )}

      {/* LoRa radio panel */}
      {showRadioPanel && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 flex items-end sm:items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => setShowRadioPanel(false)}
          role="dialog"
          aria-modal="true"
          aria-label="LoRa radio"
        >
          <div className="w-full max-w-md max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <DeviceSetupPanel
              onClose={() => setShowRadioPanel(false)}
              roomId={roomId ?? undefined}
              roomSecret={roomSecret || undefined}
            />
          </div>
        </div>
      )}

      {/* Vehicle Sighting Form Modal (not in the App Store build) */}
      {ENFORCEMENT_REPORTS && location && (
        <SightingForm
          isOpen={showSightingForm}
          onClose={() => setShowSightingForm(false)}
          onSubmit={handleSightingSubmit}
          location={fuzzLocation(location)}
          senderId={identity?.peerId ?? ''}
          senderName={displayName}
        />
      )}

    </div>
  )
}
